import { useCallback, useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import {
  Chevron,
  Option,
  OptionsContainer,
  OptionsList,
  Placeholder,
  SearchInput,
  SecondaryLabel,
  Trigger,
  TriggerText,
  Wrapper,
} from './Dropdown.styles';
import type { DropdownProps } from './Dropdown.types';

export function Dropdown({
  options,
  value,
  placeholder = 'Select',
  onChange,
  disabled,
  variant = 'default',
  align = 'right',
  'aria-label': ariaLabel,
  renderValue,
  withSearch,
  searchPlaceholder = 'Search...',
  hasError,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);
  useClickOutside(wrapperRef, handleClose, isOpen);

  const filteredOptions =
    withSearch && search
      ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : options;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const selectedIdx = filteredOptions.findIndex((o) => o.value === value);
      setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
      if (withSearch) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  }, [isOpen, filteredOptions, value, withSearch]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const items = listRef.current?.querySelectorAll('[role="option"]');
    if (items && items[activeIndex]) {
      (items[activeIndex] as HTMLElement).scrollIntoView?.({ block: 'nearest' });
    }
  }, [isOpen, activeIndex]);

  const selectedOption = options.find((o) => o.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch('');
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setActiveIndex((prev) => {
          let next = prev + 1;
          while (next < filteredOptions.length && filteredOptions[next].disabled) next++;
          return next < filteredOptions.length ? next : prev;
        });
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setActiveIndex((prev) => {
          let next = prev - 1;
          while (next >= 0 && filteredOptions[next].disabled) next--;
          return next >= 0 ? next : prev;
        });
        break;
      }
      case 'Home': {
        e.preventDefault();
        const first = filteredOptions.findIndex((o) => !o.disabled);
        if (first >= 0) setActiveIndex(first);
        break;
      }
      case 'End': {
        e.preventDefault();
        for (let i = filteredOptions.length - 1; i >= 0; i--) {
          if (!filteredOptions[i].disabled) {
            setActiveIndex(i);
            break;
          }
        }
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (activeIndex >= 0 && !filteredOptions[activeIndex].disabled) {
          handleSelect(filteredOptions[activeIndex].value);
        }
        break;
      }
    }
  };

  const activeOptionId =
    isOpen && activeIndex >= 0
      ? `dropdown-option-${filteredOptions[activeIndex]?.value}`
      : undefined;

  return (
    <Wrapper ref={wrapperRef} data-open={isOpen}>
      <Trigger
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        data-open={isOpen}
        data-variant={variant}
        disabled={disabled}
        $variant={variant}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-activedescendant={activeOptionId}
        aria-invalid={hasError || undefined}
        data-error={hasError || undefined}
      >
        <TriggerText>
          {selectedOption ? (
            renderValue ? (
              renderValue(selectedOption)
            ) : (
              selectedOption.label
            )
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
        </TriggerText>
        <Chevron $open={isOpen}>
          <ChevronDownIcon size={14} />
        </Chevron>
      </Trigger>
      {isOpen && (
        <OptionsList ref={listRef} role="listbox" aria-label={ariaLabel} data-align={align}>
          {withSearch && (
            <SearchInput
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              aria-label="Search options"
            />
          )}
          <OptionsContainer>
            {filteredOptions.map((option, index) => (
              <Option
                key={option.value}
                id={`dropdown-option-${option.value}`}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
                data-selected={option.value === value}
                data-disabled={option.disabled}
                data-active={index === activeIndex}
                onClick={() => !option.disabled && handleSelect(option.value)}
              >
                {option.label}
                {option.secondaryLabel && <SecondaryLabel>{option.secondaryLabel}</SecondaryLabel>}
              </Option>
            ))}
          </OptionsContainer>
        </OptionsList>
      )}
    </Wrapper>
  );
}
